import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import SEO from '@/components/base/SEO';
import Breadcrumb from '@/components/base/Breadcrumb';
import { articles } from '@/mocks/kunlun-data';

const articleImages: Record<string, string> = {
  'article-glacier-health': 'https://readdy.ai/api/search-image?query=Elegant%20editorial%20photography%20of%20pure%20glacier%20water%20in%20a%20transparent%20glass%20on%20a%20clean%20white%20surface%20with%20natural%20sunlight%2C%20minimalist%20lifestyle%20aesthetic%2C%20premium%20wellness%20brand%20feel&width=1200&height=600&seq=article-glacier-health&orientation=landscape',
  'article-brand-story': 'https://readdy.ai/api/search-image?query=Majestic%20Kunlun%20mountain%20range%20with%20ancient%20glaciers%20under%20golden%20sunrise%20light%2C%20pristine%20snow%20covered%20peaks%2C%20ethereal%20mist%2C%20cinematic%20aerial%20landscape&width=1200&height=600&seq=article-brand-story&orientation=landscape',
  'article-guide': 'https://readdy.ai/api/search-image?query=A%20collection%20of%20different%20premium%20mineral%20water%20bottles%20arranged%20elegantly%20on%20a%20white%20marble%20surface%20with%20natural%20lighting%2C%20lifestyle%20product%20photography&width=1200&height=600&seq=article-guide&orientation=landscape',
  'article-culture': 'https://readdy.ai/api/search-image?query=Traditional%20Chinese%20ink%20painting%20style%20depicting%20the%20sacred%20Kunlun%20mountains%20with%20flowing%20waterfalls%2C%20misty%20atmosphere%2C%20ethereal%20spiritual%20aesthetic&width=1200&height=600&seq=article-culture&orientation=landscape',
  'article-compare': 'https://readdy.ai/api/search-image?query=Two%20glasses%20of%20water%20side%20by%20side%20on%20a%20minimal%20white%20table%2C%20one%20with%20glacier%20ice%20cubes%2C%20scientific%20comparison%20aesthetic%2C%20clean%20laboratory%20lighting&width=1200&height=600&seq=article-compare&orientation=landscape',
  'article-awards': 'https://readdy.ai/api/search-image?query=A%20premium%20mineral%20water%20bottle%20next%20to%20three%20golden%20award%20medals%20on%20a%20white%20pedestal%20with%20dramatic%20spotlight%2C%20prestigious%20awards%20feel&width=1200&height=600&seq=article-awards&orientation=landscape',
};

export default function ArticleDetailPage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="pt-32 text-center">
        <h1 className="font-heading text-2xl text-foreground-950">未找到该文章</h1>
        <Link to="/articles" className="text-primary-600 hover:text-primary-700 mt-4 inline-block cursor-pointer whitespace-nowrap">
          返回文章列表
        </Link>
      </div>
    );
  }

  const paragraphs = article.content.split('\n\n').filter(Boolean);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    publisher: {
      '@type': 'Organization',
      name: '昆仑雪',
    },
  };

  return (
    <>
      <SEO
        title={article.title}
        description={article.excerpt}
        keywords={`昆仑雪,${article.category},冰川水,高端矿泉水`}
        canonicalUrl={`https://kunlunsnow.com/articles/${article.slug}`}
        ogType="article"
      />
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <main>
        <section className="pt-24 pb-10 md:pt-28 md:pb-14 px-4 md:px-6 lg:px-10 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: '文章资讯', path: '/articles' },
                { label: article.title },
              ]}
            />
          </div>
        </section>

        <article className="pb-16 md:pb-24 px-4 md:px-6 lg:px-10 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <div className="mb-8">
              <span className="text-xs px-2.5 py-1 bg-secondary-100 text-secondary-700 rounded-full font-medium whitespace-nowrap inline-block mb-4">
                {article.category}
              </span>
              <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 mb-4 leading-tight">
                {article.title}
              </h1>
              <div className="flex items-center gap-4 text-xs text-foreground-400 flex-wrap">
                <span>{article.author}</span>
                <span className="text-foreground-300">|</span>
                <span>{article.date}</span>
                <span className="text-foreground-300">|</span>
                <span>{article.readTime} 阅读</span>
              </div>
            </div>

            <div className="rounded-2xl overflow-hidden mb-10">
              <img
                src={articleImages[article.imageSeq]}
                alt={article.title}
                title={article.title}
                className="w-full h-auto object-cover"
              />
            </div>

            <div className="prose-custom">
              {paragraphs.map((paragraph, index) => {
                const isHeading = paragraph.trim().match(/^[一二三四五六七八九十]、/);
                if (isHeading) {
                  return (
                    <h2 key={index} className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mt-10 mb-4">
                      {paragraph.trim()}
                    </h2>
                  );
                }
                return (
                  <p key={index} className="text-sm md:text-base text-foreground-600 leading-relaxed mb-5">
                    {paragraph.trim()}
                  </p>
                );
              })}
            </div>

            <div className="mt-12 pt-8 border-t border-background-200/70">
              <Link
                to="/articles"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-left-line"></i>
                返回文章列表
              </Link>
            </div>
          </div>
        </article>
      </main>
    </>
  );
}